import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {Actions} from "../../core";

const URL_SPLIT = /(https?:\/\/[^\s)]+)/g;
// strip markdown leftovers and tighten spacing before rendering
const cleanBotText = (t) => t
  .replace(/\*\*(.+?)\*\*/g, '$1')
  .replace(/^\s*[*-]\s+/gm, '•  ')
  .replace(/\n{3,}/g, '\n\n')
  .trim();
// split with a capture group puts URLs at odd indices;
// URLs render as a compact "view" button instead of the raw address
const linkify = (text) =>
  cleanBotText(text).split(URL_SPLIT).map((part, i) =>
    i % 2 === 1 ?
      <a key={i} className="ai-view-btn" href={encodeURI(part)} target="_blank" rel="noopener noreferrer">VIEW</a>
      : part
  );

const chatStyles = `
.search-sec .ai-chat-box{display:flex;flex-direction:column;height:100%;width:100%;padding:0;box-sizing:border-box;overflow:hidden}
.search-sec .ai-chat-title{font-size:12px;letter-spacing:3px;opacity:.55;padding:14px 16px 10px;text-align:center}
.search-sec .ai-chat-messages{flex:1;min-height:0;overflow-y:auto;display:flex;flex-direction:column;gap:16px;padding:8px 16px}
.search-sec .ai-empty{flex:1;min-height:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;text-align:center;padding:0 16px}
.search-sec .ai-empty-title{font-size:22px;line-height:1.4;font-weight:bold}
.search-sec .ai-empty-sub{font-size:14px;opacity:.75;font-weight:500}
.search-sec .ai-chips{display:flex;flex-wrap:wrap;gap:8px;justify-content:center}
.search-sec .ai-chip{border:1px solid rgba(0,0,0,.25);background:transparent;color:inherit;border-radius:0;padding:8px 14px;font-size:13px;cursor:pointer;transition:background .15s}
.search-sec .ai-chip:hover{background:rgba(0,0,0,.06)}
.search-sec .ai-msg{font-size:14px;line-height:1.55;font-weight:500;white-space:pre-wrap;word-break:break-word}
.search-sec .ai-msg a{color:inherit;text-decoration:underline}
.search-sec .ai-msg a.ai-view-btn{display:inline-block;margin-left:8px;padding:1px 8px;border:1px solid rgba(0,0,0,.45);font-size:10px;letter-spacing:1.5px;font-weight:bold;text-decoration:none;line-height:1.6;vertical-align:1px;transition:background .15s,color .15s}
.search-sec .ai-msg a.ai-view-btn:hover{background:#000;color:#fff}
.dark .search-sec .ai-msg a.ai-view-btn{border-color:rgba(255,255,255,.5)}
.dark .search-sec .ai-msg a.ai-view-btn:hover{background:#e0e0e0;color:#131314}
.search-sec .ai-msg-user{align-self:flex-end;max-width:75%;background:rgba(0,0,0,.08);border-radius:0;padding:10px 16px}
.search-sec .ai-msg-bot{align-self:flex-start;max-width:100%;padding:0 2px}
.search-sec .ai-msg-sources{margin-top:10px;display:flex;flex-direction:column;gap:10px}
.search-sec .ai-src-group{display:flex;flex-direction:column;gap:4px}
.search-sec .ai-src-cat{font-size:11px;letter-spacing:2px;text-transform:uppercase;opacity:.65;font-weight:bold}
.search-sec .ai-msg-sources a{font-size:12px;font-weight:bold;text-decoration:underline;color:inherit;opacity:.85}
.search-sec .ai-msg-sources a:hover{opacity:1}
.search-sec .ai-chat-form{display:flex;align-items:stretch;gap:0;margin:12px 0 0;width:100%}
.search-sec .ai-chat-form input{flex:1;width:100%;opacity:1!important;border:1px solid rgba(0,0,0,.3)!important;border-right:none!important;border-left:none!important;border-bottom:none!important;border-radius:0;background:transparent;outline:none;font-size:16px!important;font-weight:normal!important;padding:14px 16px!important;font-family:inherit!important;color:inherit}
.search-sec .ai-chat-form input::placeholder{text-transform:none!important;font-weight:normal!important;font-size:16px!important;opacity:.5!important;color:inherit!important}
.search-sec .ai-send-btn{min-width:64px;border-radius:0;border:1px solid #000;background:#000;color:#fff;font-size:17px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:opacity .15s}
.search-sec .ai-send-btn:disabled{opacity:.25;cursor:default}
.search-sec .ai-typing{display:inline-flex;align-items:center;gap:5px;padding:6px 0}
.search-sec .ai-typing i{width:7px;height:7px;border-radius:50%;background:currentColor;opacity:.4;animation:aiTyping 1.2s infinite}
.search-sec .ai-typing i:nth-child(2){animation-delay:.2s}
.search-sec .ai-typing i:nth-child(3){animation-delay:.4s}
@keyframes aiTyping{0%,80%,100%{opacity:.25;transform:translateY(0)}40%{opacity:1;transform:translateY(-4px)}}
.dark .search-sec .ai-chat-box{color:#e0e0e0}
.dark .search-sec .ai-chip{border-color:rgba(255,255,255,.35)}
.dark .search-sec .ai-chip:hover{background:rgba(255,255,255,.1)}
.dark .search-sec .ai-msg-user{background:rgba(255,255,255,.14)}
.dark .search-sec .ai-chat-form input{border-color:rgba(255,255,255,.35)!important;color:#e0e0e0}
.dark .search-sec .ai-send-btn{background:#e0e0e0;color:#131314;border-color:#e0e0e0}
`;

const SUGGESTIONS = [
  "Upcoming events",
  "Artists",
  "Podcasts",
  "Releases",
  "Jobs",
  "House rules",
];

export const SearchPopupAi = (props)=>{

  const dispatch = useDispatch();
  const popup  = useSelector(state=>state.Store.PopupWindow.modals.search)
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [asking, setAsking] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if(popup){
      setChatInput("")
      setTimeout(()=>inputRef.current?.focus(), 100)
    }
  }, [popup]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({behavior:"smooth"})
  }, [messages, asking]);

  const send = (text) => {
    const question = (text ?? chatInput).trim();
    if(asking || question.length < 3) return;
    setMessages(prev=>[...prev, {role:"user", text:question}]);
    setChatInput("");
    setAsking(true);
    Actions.User.askAi(question).then(response => {
      setAsking(false);
      if(response.status && response.data?.data?.answer){
        setMessages(prev=>[...prev, {role:"bot", text:response.data.data.answer, sources:response.data.data.sources||[]}]);
      }else{
        setMessages(prev=>[...prev, {role:"bot", text:"Couldn't get an answer, please try again.", sources:[]}]);
      }
    })
  }

  return  (
		(popup) ?<div className="search-sec active">
      <style>{chatStyles}</style>
      <div className="ai-chat-box">
        <div className="ai-chat-title">BASSIANI AI</div>
        {
          messages.length===0 && !asking ?
            <div className="ai-empty">
              <div>
                <div className="ai-empty-title">How can I help? 👋</div>
                <div className="ai-empty-sub">Ask me about Bassiani events, artists, podcasts and releases</div>
              </div>
              <div className="ai-chips">
                {SUGGESTIONS.map((s,i)=>
                  <button key={i} className="ai-chip" onClick={()=>send(s)}>{s}</button>
                )}
              </div>
            </div>
            :
            <div className="ai-chat-messages">
              {
                messages.map((m,i)=>(
                  <div key={i} className={`ai-msg ${m.role==="user"?"ai-msg-user":"ai-msg-bot"}`}>
                    {m.role==="bot" ? linkify(m.text) : m.text}
                    {
                      m.sources?.length > 0 &&
                      <div className="ai-msg-sources">
                        {
                          Object.entries(m.sources.reduce((acc,s)=>{
                            (acc[s.category] = acc[s.category] || []).push(s);
                            return acc;
                          },{})).map(([category, items])=>
                            <div key={category} className="ai-src-group">
                              <div className="ai-src-cat">{category}</div>
                              {items.map((s,j)=>
                                <a key={j} href={encodeURI(`https://bassiani.com${s.url}`)} target="_blank" rel="noopener noreferrer">
                                  {s.title}
                                </a>
                              )}
                            </div>
                          )
                        }
                      </div>
                    }
                  </div>
                ))
              }
              {
                asking ?
                  <div className="ai-msg ai-msg-bot">
                    <span className="ai-typing"><i/><i/><i/></span>
                  </div> : null
              }
              <div ref={chatEndRef}/>
            </div>
        }
        <div className="ai-chat-form">
          <input ref={inputRef} type="text" placeholder="Search events, artists, podcasts…" value={chatInput}
                 onChange={e=>setChatInput(e.target.value)}
                 onKeyDown={e=>{ if(e.key==='Enter') send() }}/>
          <button className="ai-send-btn" onClick={()=>send()} disabled={asking || chatInput.trim().length<3}>↑</button>
        </div>
      </div>
		</div>:null
	)
}
